import { useCallback, useMemo, useState } from 'react'
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table'
import { DataTable, DataTableProvider } from 'components/DataTable'
import { DataTableContainer } from 'components/DataTable/UI'
import FbButton from 'components/FbUI/FbButton'
import { FileText, Plus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import {
  ActionsDropdown,
  createDeleteAction,
  createEditAction,
} from 'components/Buttons/ActionsDropdown'
import dialogConfirm from 'components/dialogConfirm'
import { useGetBrands, useDeleteBrand } from 'services/brandsApi'
import type { Brand } from 'types/brands'
import { toast } from 'react-toastify'
import { formatDateWithTimeAndMinutes2 } from 'utils/formatting'
import { BrandModal } from './BrandModal'
import * as S from '../CommonTable.styles'
import { TableSearch } from 'components/Filters/TableSearch'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterIdentifier } from 'models/saved_view'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'

const columnHelper = createColumnHelper<Brand>()

/** Renders a date value as date + time stacked vertically. */
function renderDateCell(value: string | null | undefined) {
  if (!value) return '—'
  const { date, time } = formatDateWithTimeAndMinutes2(value)
  return (
    <div className="flex flex-col">
      <span>{date}</span>
      <span className="text-gray-500 text-sm">{time}</span>
    </div>
  )
}

interface BrandsTableProps {
  tableKey: string
}

/** Inner table component with brand listing, search, create/edit/delete actions. */
function BrandsTableInner({ tableKey }: BrandsTableProps) {
  const BRANDS_IDENTIFIER = `${tableKey}_BRANDS` as FilterIdentifier
  const brandsFilterStore = filterStoreRepo.getStore(BRANDS_IDENTIFIER)
  const filtersAsParams = useFilterParams(brandsFilterStore)

  const navigate = useNavigate()

  const { data, isLoading, refetch } = useGetBrands({
    search: filtersAsParams.search as string | undefined,
    page: 1,
    page_size: 1000,
    ordering: 'name',
  })
  const deleteBrand = useDeleteBrand()

  const [showModal, setShowModal] = useState(false)
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null)

  const handleDelete = useCallback(
    async (brand: Brand) => {
      const confirmed = await dialogConfirm(
        `Are you sure you want to delete the brand "${brand.name}"?`,
        'Delete Brand'
      )
      if (!confirmed) return

      try {
        await deleteBrand.mutateAsync(brand.id)
        toast.success('Brand deleted successfully')
        refetch()
      } catch (error) {
        console.error('Error deleting brand:', error)
        toast.error('Failed to delete brand')
      }
    },
    [deleteBrand, refetch]
  )

  const columns = useMemo<ColumnDef<Brand, any>[]>(
    () => [
      columnHelper.accessor('name', {
        header: 'Brand Name',
        meta: {
          isFirstColumn: true,
          isEditable: false,
        },
        cell: (info) => info.getValue() || '—',
        size: 300,
        enableSorting: true,
        enableHiding: false,
      }),
      columnHelper.accessor('image_url', {
        header: 'Brand Image',
        meta: {
          isEditable: false,
          headerDisableOrdering: true,
        },
        cell: (info) => {
          const url = info.getValue()
          return url ? (
            <img
              src={url}
              alt={info.row.original.name}
              className="h-8 w-8 rounded object-cover"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-100 text-[10px] text-gray-400">
              —
            </div>
          )
        },
        size: 100,
        enableSorting: false,
        enableHiding: false,
      }),
      columnHelper.accessor('document_count', {
        header: 'Documents',
        cell: (info) => info.getValue() ?? 0,
        size: 110,
        enableSorting: true,
      }),
      columnHelper.accessor('variant_count', {
        header: 'Product Variants',
        cell: (info) => info.getValue() ?? 0,
        size: 110,
        enableSorting: true,
      }),
      columnHelper.accessor('created', {
        header: 'Created',
        cell: (info) => renderDateCell(info.getValue()),
        size: 160,
        enableSorting: true,
      }),
      columnHelper.accessor('modified', {
        header: 'Modified',
        cell: (info) => renderDateCell(info.getValue()),
        size: 160,
        enableSorting: true,
      }),
      {
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableHiding: false,
        meta: {
          isEditable: false,
          headerDisableOrdering: true,
          isLastColumn: true,
          rightAlign: true,
          headerGroup: 'Actions',
        },
        cell: ({ row }: CellContext<Brand, unknown>) => {
          const brand = row.original
          return (
            <ActionsDropdown
              actions={[
                createEditAction(() => {
                  setEditingBrand(brand)
                  setShowModal(true)
                }),
                {
                  label: 'View Documents',
                  icon: FileText,
                  onClick: () => {
                    const brandFilter = [
                      { value: String(brand.id), label: brand.name },
                    ]
                    // Set store state directly for already-cached stores
                    const store = filterStoreRepo.getStore(
                      'DOCUMENTS_ALL_TABLE'
                    )
                    store.setState({
                      valueFilters: { brand: brandFilter },
                      pendingValueFilters: { brand: brandFilter },
                      criteria: 'all' as const,
                      pendingCriteria: 'all' as const,
                      page: 1,
                      filter_id: undefined,
                    })
                    // Also pass URL params so freshly-created stores pick
                    // up the filter during async persist rehydration
                    const filterState = JSON.stringify({
                      valueFilters: { brand: brandFilter },
                      criteria: 'all',
                    })
                    navigate(
                      `/documents/all?DOCUMENTS_ALL_TABLE=${encodeURIComponent(filterState)}`
                    )
                  },
                },
                createDeleteAction(() => handleDelete(brand)),
              ]}
              containerWidth={170}
            />
          )
        },
      },
    ],
    [handleDelete, navigate, brandsFilterStore]
  )

  const tableData = useMemo(() => data?.results ?? [], [data?.results])

  return (
    <>
      <DataTable
        tableKey={tableKey}
        tableHeader={
          <S.SearchContainer>
            <S.SearchContainerFilters>
              <S.TableSearchContainer>
                <TableSearch
                  filterStore={brandsFilterStore}
                  searchPlaceholder="Search brands..."
                  tableKey={tableKey}
                />
              </S.TableSearchContainer>
              <S.TableButtonsContainer>
                <FbButton
                  onClick={() => {
                    setEditingBrand(null)
                    setShowModal(true)
                  }}
                >
                  <Plus size={16} className="mr-1" />
                  Create Brand
                </FbButton>
              </S.TableButtonsContainer>
            </S.SearchContainerFilters>
          </S.SearchContainer>
        }
        data={tableData}
        columns={columns}
        loading={isLoading}
        enableRowSelection={false}
        virtualizeRows={false}
        isPaginationEnabled={false}
      />

      <BrandModal
        show={showModal}
        onHide={() => {
          setShowModal(false)
          setEditingBrand(null)
        }}
        brand={editingBrand}
      />
    </>
  )
}

/** Brand management table with DataTable provider wrapper. */
export function BrandsTable({ tableKey }: BrandsTableProps) {
  return (
    <ColumnsStoreProvider tableKey={tableKey}>
      <DataTableProvider tableKey={tableKey}>
        <DataTableContainer>
          <BrandsTableInner tableKey={tableKey} />
        </DataTableContainer>
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
